// AdjustSettings.js
import React, { useState, useEffect, useContext } from 'react';
import { X } from 'lucide-react';
import { getProjectDetails, updateProject, updateProjectStyle } from '../services/database';
import { ProjectContext } from '../context/ProjectContext';
import StyleSettings from './StyleSettings';
import { getUserCustomPalettes, getCustomPaletteById } from '../services/customPalettes';

const AdjustSettings = ({ projectId, onClose }) => {
  const [activeTab, setActiveTab] = useState('description');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    userProfile: ''
  });
  const [styleChoices, setStyleChoices] = useState({
    device: 'Desktop',
    theme: 'light',
    palette: 'Classic Light',
    font: 'Native UI',
    isCustomPalette: false,
    customPaletteId: null
  });
  const [originalStyleChoices, setOriginalStyleChoices] = useState(null);
  const [isDescriptionChanged, setIsDescriptionChanged] = useState(false);
  const [isStyleChanged, setIsStyleChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [customPalettes, setCustomPalettes] = useState([]);
  const { dispatch } = useContext(ProjectContext);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        setIsLoading(true);
        
        // Fetch project details
        const projectDetails = await getProjectDetails(projectId);
        setFormData({
          title: projectDetails.title || '',
          description: projectDetails.description || '',
          userProfile: projectDetails.userProfile || ''
        });
        
        // Set style choices if available
        if (projectDetails.styleChoices) {
          const choices = {
            device: projectDetails.styleChoices.device || 'Desktop',
            theme: projectDetails.styleChoices.theme || 'light',
            palette: projectDetails.styleChoices.palette || 'Classic Light',
            font: projectDetails.styleChoices.font || 'Native UI',
            isCustomPalette: projectDetails.styleChoices.isCustomPalette || false,
            customPaletteId: projectDetails.styleChoices.customPaletteId || null,
            customPaletteColors: projectDetails.styleChoices.customPaletteColors || null
          };
          
          // If using a custom palette but we don't have colors stored, try to fetch them
          if (choices.isCustomPalette && choices.customPaletteId && !choices.customPaletteColors) {
            try {
              const customPalette = await getCustomPaletteById(choices.customPaletteId);
              if (customPalette) {
                choices.customPaletteColors = {
                  hex: customPalette.palette,
                  tailwind: customPalette.tailwindPalette
                };
              }
            } catch (error) {
              console.error('Error fetching custom palette:', error);
            }
          }
          
          setStyleChoices(choices);
          setOriginalStyleChoices(choices); // Store the original values
        }
        
        // Fetch custom palettes
        const userCustomPalettes = await getUserCustomPalettes();
        setCustomPalettes(userCustomPalettes);
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching project details:', error);
        setIsLoading(false);
      }
    };

    fetchProjectDetails();
  }, [projectId]);

  const handleDescriptionChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevFormData => {
      const newFormData = { ...prevFormData, [name]: value };
      setIsDescriptionChanged(true);
      return newFormData;
    });
  };

  const handleStyleChange = (newStyleChoices) => {
    // Ensure we preserve the device setting from original style choices
    const updatedStyleChoices = {
      ...newStyleChoices,
      device: originalStyleChoices?.device || 'Desktop'
    };
    
    setStyleChoices(updatedStyleChoices);
    setIsStyleChanged(true);
  };

  const handleSave = async () => {
    try {
      // Save description changes if any
      if (isDescriptionChanged) {
        await updateProject(projectId, formData);
        dispatch({ type: 'SET_PROJECT_TITLE', payload: formData.title });
      }
      
      // Save style changes if any, ensuring device value is preserved
      if (isStyleChanged) {
        const styleToUpdate = {
          ...styleChoices,
          device: originalStyleChoices?.device || styleChoices.device // Make sure to use original device
        };
        
        // If this is a custom palette, include the customPaletteColors if available
        if (styleToUpdate.isCustomPalette && styleToUpdate.customPaletteId) {
          // If we already have colors stored, use them
          if (originalStyleChoices?.customPaletteColors) {
            styleToUpdate.customPaletteColors = originalStyleChoices.customPaletteColors;
          } 
          // Otherwise, try to find the palette in our loaded custom palettes
          else {
            const selectedPalette = customPalettes.find(p => p._id === styleToUpdate.customPaletteId);
            if (selectedPalette) {
              styleToUpdate.customPaletteColors = {
                hex: selectedPalette.palette,
                tailwind: selectedPalette.tailwindPalette
              };
            }
          }
        }
        
        await updateProjectStyle(projectId, styleToUpdate);
        dispatch({ type: 'SET_STYLE_CHOICES', payload: styleToUpdate });
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating project:', error);
      // Handle error
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header with title and close button */}
        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center z-20">
          <h2 className="text-2xl font-bold">Adjust Settings</h2>
          <button 
            onClick={onClose} 
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b">
          <button
            className={`px-6 py-3 font-medium ${
              activeTab === 'description' 
                ? 'border-b-2 border-black text-black' 
                : 'text-gray-500 hover:text-black'
            }`}
            onClick={() => handleTabChange('description')}
          >
            Description
          </button>
          <button
            className={`px-6 py-3 font-medium ${
              activeTab === 'style' 
                ? 'border-b-2 border-black text-black' 
                : 'text-gray-500 hover:text-black'
            }`}
            onClick={() => handleTabChange('style')}
          >
            Style
          </button>
        </div>
        
        {/* Tab Content */}
        <div className="p-6 flex-1 overflow-hidden">
          {/* Description Tab - Hidden when not active */}
          <div className={`${activeTab === 'description' ? 'block' : 'hidden'} h-full`}>
            <div className="space-y-4">
              <div>
                <label className="block text-lg font-semibold mb-1">Project Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleDescriptionChange}
                  className="w-full p-3 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-lg font-semibold mb-1">Project Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  rows={4}
                  className="w-full p-3 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-lg font-semibold mb-1">User Profile</label>
                <textarea
                  name="userProfile"
                  value={formData.userProfile}
                  onChange={handleDescriptionChange}
                  rows={4}
                  className="w-full p-3 border rounded-md"
                />
              </div>
            </div>
          </div>
          
          {/* Style Tab - Hidden when not active */}
          <div className={`${activeTab === 'style' ? 'block' : 'hidden'} h-full`}>
            <StyleSettings 
              initialChoices={styleChoices} 
              onChange={handleStyleChange} 
            />
          </div>
        </div>
        
        {/* Footer with action buttons */}
        <div className="sticky bottom-0 bg-white p-4 border-t flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-md bg-gray-200 text-black hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isDescriptionChanged && !isStyleChanged}
            className={`px-6 py-2 rounded-md text-white ${
              (isDescriptionChanged || isStyleChanged) 
                ? 'bg-black hover:bg-gray-800' 
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdjustSettings;